/**
 * Hourly Consumption Calculator
 *
 * @description Calculates hourly consumption distribution from monthly totals
 * @version 1.0.0
 * @created 2025-10-27
 * @author eCompanion2 CSV Export Feature
 *
 * Purpose: Convert monthly consumption values and hourly patterns into
 * granular 8760-hour (or 8784 for leap year) consumption data for CSV export.
 *
 * Features:
 * - Distributes monthly totals across days based on hourly patterns
 * - Differentiates weekday vs weekend consumption
 * - Integrates Polish holiday calendar
 * - Normalizes to exact monthly totals
 * - Supports leap years
 */

import { getDayType } from '../holidays';

/**
 * Hourly Consumption Data Point
 */
export interface HourlyConsumptionData {
  /** Full date-time for this hour */
  date: Date;

  /** Hour of day (0-23) */
  hour: number;

  /** Consumption for this hour in kWh */
  consumption: number;

  /** Day type classification */
  dayType: 'weekday' | 'weekend' | 'holiday';
}

/**
 * Hourly Configuration (Pattern)
 */
export interface HourlyConfig {
  /** Weekday hourly percentages (24 values, 0-100) */
  weekday: number[];

  /** Weekend hourly percentages (24 values, 0-100) */
  weekend: number[];
}

/**
 * Days in Month (Non-Leap Year)
 */
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * Days in Month (Leap Year)
 */
const DAYS_IN_MONTH_LEAP = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * Check if year is a leap year
 *
 * @param year - Year to check
 * @returns True if leap year, false otherwise
 */
function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

/**
 * Calculate hourly consumption from monthly totals
 *
 * Algorithm:
 * 1. For each day of the year:
 *    - Determine day type (weekday, weekend, holiday)
 *    - Get appropriate hourly pattern
 * 2. For each hour of each day:
 *    - Calculate hourly percentage from pattern
 *    - Distribute month's total consumption proportionally
 * 3. Normalize to ensure exact monthly totals
 *
 * @param monthlyConsumption - Array of 12 monthly consumption values (kWh)
 * @param hourlyConfig - Hourly consumption patterns
 * @param year - Year for calculations (for holiday detection)
 * @returns Array of 8760 (or 8784) hourly consumption data points
 *
 * @example
 * const hourly = calculateHourlyConsumption(
 *   [300, 280, 260, 240, 220, 200, 220, 240, 260, 280, 300, 320],
 *   {
 *     weekday: [0.5, 0.5, ..., 2.0],  // 24 values
 *     weekend: [0.8, 0.7, ..., 1.2]   // 24 values
 *   },
 *   2025
 * );
 * // Returns: [
 * //   { date: Date(2025-01-01 00:00), hour: 0, consumption: 0.403, dayType: 'holiday' },
 * //   ...
 * // ]
 */
export function calculateHourlyConsumption(
  monthlyConsumption: number[],
  hourlyConfig: HourlyConfig,
  year: number
): HourlyConsumptionData[] {
  // Validate inputs
  if (monthlyConsumption.length !== 12) {
    throw new Error('Monthly consumption must have exactly 12 values (Jan-Dec)');
  }

  if (hourlyConfig.weekday.length !== 24 || hourlyConfig.weekend.length !== 24) {
    throw new Error('Hourly config must have exactly 24 values for weekday and weekend');
  }

  const result: HourlyConsumptionData[] = [];
  const daysPerMonth = isLeapYear(year) ? DAYS_IN_MONTH_LEAP : DAYS_IN_MONTH;

  // Process each month
  for (let month = 0; month < 12; month++) {
    const monthlyTotal = monthlyConsumption[month];
    const daysInMonth = daysPerMonth[month];

    // Handle zero consumption edge case
    if (monthlyTotal === 0) {
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        const dayTypeValue = getDayType(date);

        for (let hour = 0; hour < 24; hour++) {
          result.push({
            date: new Date(year, month, day, hour),
            hour,
            consumption: 0,
            dayType: dayTypeValue
          });
        }
      }
      continue;
    }

    // Step 1: Calculate total weight for the month
    let totalWeight = 0;

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayTypeValue = getDayType(date);
      const isWeekendOrHoliday = dayTypeValue === 'weekend' || dayTypeValue === 'holiday';
      const patternArray = isWeekendOrHoliday ? hourlyConfig.weekend : hourlyConfig.weekday;

      for (let hour = 0; hour < 24; hour++) {
        // Convert percentage (0-100) to decimal multiplier (0-1)
        totalWeight += patternArray[hour] / 100;
      }
    }

    // Step 2: Calculate consumption for each hour with normalization
    const normalizationFactor = monthlyTotal / totalWeight;

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayTypeValue = getDayType(date);
      const isWeekendOrHoliday = dayTypeValue === 'weekend' || dayTypeValue === 'holiday';
      const patternArray = isWeekendOrHoliday ? hourlyConfig.weekend : hourlyConfig.weekday;

      for (let hour = 0; hour < 24; hour++) {
        // Calculate hourly consumption
        // Pattern is percentage (0-100), convert to decimal and multiply by normalization factor
        const consumption = (patternArray[hour] / 100) * normalizationFactor;

        result.push({
          date: new Date(year, month, day, hour),
          hour,
          consumption: Math.round(consumption * 10000) / 10000, // Round to 4 decimals
          dayType: dayTypeValue
        });
      }
    }
  }

  return result;
}

/**
 * Get total number of hours in year
 *
 * @param year - Year to check
 * @returns 8760 for non-leap year, 8784 for leap year
 */
export function getHoursInYear(year: number): number {
  return isLeapYear(year) ? 8784 : 8760;
}

/**
 * Validate that hourly consumption matches monthly totals
 *
 * Utility function for testing and debugging.
 *
 * @param hourlyData - Hourly consumption data
 * @param monthlyConsumption - Expected monthly totals
 * @returns Array of monthly differences (should be near zero)
 */
export function validateMonthlyTotals(
  hourlyData: HourlyConsumptionData[],
  monthlyConsumption: number[]
): number[] {
  const monthlyActual = new Array(12).fill(0);

  for (const dataPoint of hourlyData) {
    const month = dataPoint.date.getMonth();
    monthlyActual[month] += dataPoint.consumption;
  }

  // Calculate differences
  return monthlyConsumption.map((expected, month) => {
    const actual = Math.round(monthlyActual[month] * 100) / 100;
    const expectedRounded = Math.round(expected * 100) / 100;
    return Math.round((actual - expectedRounded) * 100) / 100;
  });
}

/**
 * Default export
 */
export default {
  calculateHourlyConsumption,
  getHoursInYear,
  validateMonthlyTotals
};
